import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { requirePermission } from '../access';
import { queryKeys } from '@/lib/query-keys';
import { capabilityQueryOptions } from '../capability';
import { sessionQueryOptions } from './session-api';
import type { AuthSession } from '@/lib/auth-client';

/**
 * The session, or a redirect to the login page that remembers where the user was going.
 *
 * Runs in `beforeLoad`, which is the whole point: a component that checked the session
 * itself would render the protected page for one frame before bouncing, and the redirect
 * target would be lost.
 *
 * A failure other than "nobody is signed in" (a 500, an unreachable API) is *not* turned
 * into a login redirect — that would tell an operator their credentials are wrong while the
 * backend is down. It propagates and the route's error boundary reports it.
 */
export const requireSession = async (
  client: QueryClient,
  href: string,
): Promise<AuthSession> => {
  const session = await client.fetchQuery(sessionQueryOptions);
  if (session === null) {
    throw redirect({ to: '/login', search: { redirect: href } });
  }
  return session;
};

/** Where an account that still owes a password change is sent (`src/routes/_authenticated/set-password.tsx`). */
export const SET_PASSWORD_PATH = '/set-password';

/**
 * The forced-password-change gate.
 *
 * The API answers 403 `PASSWORD_CHANGE_REQUIRED` to every business route while the account
 * still carries an initial password an administrator handed out; only the native better-auth
 * routes stay open (`POST /api/auth/change-password` among them), which is what the
 * set-password screen uses. The capability probe reads `GET /auth/capabilities`, so it must run
 * *after* this check — during that window a 403 means "finish your password change", not
 * "you are not an admin", and the shell would hide the very screen the operator needs.
 *
 * `pathname` is compared instead of `href` so the `redirect` query string does not make the
 * set-password page look like a different page and loop the redirect back onto itself.
 */
export const requirePasswordChanged = (
  session: AuthSession,
  href: string,
  pathname: string,
): void => {
  if (session.user.mustChangePassword !== true) return;
  if (pathname === SET_PASSWORD_PATH) return;
  throw redirect({
    to: SET_PASSWORD_PATH,
    search: { redirect: href },
  });
};

/** Whether the actor may read the user list; see `capability.ts` for why this is a probe. */
export const loadCanManageUsers = (client: QueryClient): Promise<boolean> =>
  client.ensureQueryData(capabilityQueryOptions());

/**
 * The guard for a route that renders the user list.
 *
 * A 403 is an answer, not an error: it sends the operator to the "no permission" page
 * rather than an error boundary, because nothing is broken.
 */
export const requireCanManageUsers = async (
  client: QueryClient,
  href: string,
): Promise<void> => {
  const resource = /^\/system\/(users|dicts|configs)(?:\/|\?|$)/.exec(href)?.[1];
  if (resource) return requirePermission(client, href, `${resource}:read`);
  if (await loadCanManageUsers(client)) return;
  throw redirect({ to: '/forbidden', search: { from: href } });
};

/** Drops the cached session so the next navigation re-reads it. */
export const invalidateSession = (client: QueryClient): Promise<void> =>
  client.invalidateQueries({ queryKey: queryKeys.session });
